<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ParticipantType } from '@/modules/expert/events/schemas/schemas'
import { format as dfFormat, parseISO, isValid } from 'date-fns'
import { getRandomBackgroundColorHex } from '@/modules/expert/events/utils/randomBackground'

type Props<T = unknown> = {
  participant?: ParticipantType
  contacts?: boolean
  items?: T[]
  showCheckbox?: boolean
  selected?: boolean
  disabled?: boolean
  importContacts?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:selected', value: boolean): void
}>()

const { t } = useI18n()
const randomColor = ref(getRandomBackgroundColorHex())

const model = computed({
  get: () => props.selected ?? false,
  set: (val: boolean) => emit('update:selected', val),
})

const status = computed(() => props.participant?.status || '')

const participantType = computed(() =>
  props.participant?.format === 'PUBLIC'
    ? t('events.registration.public')
    : t('events.registration.private')
)

const totalLabel = computed(() =>
  props.participant?.maxPerson
    ? `${props.participant?.currentPerson ?? 0}/${props.participant?.maxPerson}`
    : `${props.participant?.currentPerson ?? 0}`
)

const isFull = computed(() =>
  props.participant?.maxPerson
    ? Number(props.participant?.currentPerson) ===
      Number(props.participant?.maxPerson)
    : false
)

const hasOverbooking = computed(() =>
  props.participant?.maxPerson
    ? Number(props.participant?.currentPerson) >
      Number(props.participant?.maxPerson)
    : false
)

const translatedStatus = computed(() =>
  t(`events.status.${String(status.value).toLowerCase()}`)
)

const translatedFormLabel = computed(
  () => props.participant?.formLabel || t('events.participant.pattern')
)

const sideColor = computed(() => props.participant?.color || randomColor.value)

function formatDateTime(value?: string | Date | null) {
  if (!value) return '--'
  const date = typeof value === 'string' ? parseISO(value) : value
  return isValid(date) ? dfFormat(date, 'HH:mm - dd/MM/yyyy') : '--'
}
</script>

<template>
  <!-- Wrapper (permite background quando selecionado e checkbox fora do card) -->
  <div
    class="ptc-wrapper d-flex w-100"
    :class="{ 'ptc-wrapper--selected': !!selected }"
  >
    <!-- Checkbox -->
    <div v-if="showCheckbox" class="ptc-checkbox d-flex align-center">
      <v-checkbox
        v-model="model"
        :disabled="disabled"
        hide-details
        density="compact"
        class="ma-0 pa-0"
        @click.stop
      />
    </div>

    <!-- Card -->
    <v-card
      class="ptc-card elevation-2 d-flex flex-column w-100"
      rounded="lg"
      :class="[
        contacts ? 'mr-2' : '',
        importContacts ? 'ml-2 mr-0' : 'mx-4',
        disabled ? 'ptc-card--disabled' : '',
      ]"
    >
      <div class="d-flex">
        <!-- Barra lateral (4px) -->
        <div
          v-if="participant"
          class="ptc-side"
          :style="{ backgroundColor: sideColor }"
        />

        <!-- Conteúdo (padding 16px, gaps 8px) -->
        <div class="ptc-content d-flex flex-column flex-grow-1">
          <!-- Header: Nome + menu (2 linhas, tag abaixo do nome) -->
          <div class="ptc-header">
            <!-- Nome -->
            <div class="ptc-name">
              <template v-if="participant">
                <span class="ptc-title">
                  {{ participant.name }}
                </span>
                <slot name="tags" />
              </template>
              <template v-else>
                <slot name="name" />
              </template>
            </div>

            <!-- Tag -->
            <div class="ptc-tag">
              <slot name="inviteType" />
            </div>

            <!-- Ações -->
            <div class="ptc-actions">
              <slot name="menu" />
              <slot name="chevron" />
            </div>
          </div>

          <!-- Capacidade -->
          <div v-if="participant?.maxPerson" class="ptc-caption d-flex ga-1">
            <span>{{ participantType }} -</span>
            <span
              :class="{
                'text-error': hasOverbooking,
                'text-success': isFull,
              }"
            >
              {{ totalLabel }}
            </span>
          </div>
          <v-divider :thickness="1" class="border-opacity-100" />

          <!-- Slot livre (conteúdo do card) -->
          <slot name="invitecontent" />

          <!-- Datas -->
          <div v-if="participant" class="ptc-dates ptc-caption">
            <div class="ptc-date-col">
              <div class="ptc-label">
                {{ t('events.registration.startLabel') }}
              </div>
              <div class="truncate">
                {{ formatDateTime(participant.startDate) }}
              </div>
            </div>

            <div class="ptc-date-col">
              <div class="ptc-label">
                {{ t('events.registration.endLabel') }}
              </div>
              <div class="truncate">
                {{ formatDateTime(participant.endDate) }}
              </div>
            </div>
          </div>

          <!-- Status / ação -->
          <div v-if="participant" class="ptc-bottom d-flex align-center">
            <div class="ptc-status">
              <slot name="status">
                <v-select
                  :model-value="translatedStatus"
                  class="bg-grey-lighten-3"
                  density="compact"
                  hide-details
                  variant="solo"
                  readonly
                  :items="[translatedStatus]"
                />
              </slot>
            </div>

            <div class="ptc-action">
              <slot name="action-button">
                <v-btn variant="tonal" class="ptc-pill">
                  <v-icon start size="18" icon="mdi-open-in-new" />
                  {{ translatedFormLabel }}
                </v-btn>
              </slot>
            </div>
          </div>

          <slot name="links" />
        </div>
      </div>
    </v-card>
  </div>
</template>

<style scoped>
/* ====== Layout geral / spacing (Figma) ====== */
.ptc-wrapper {
  border-radius: 8px;
}
.ptc-wrapper--selected {
  background: #f1f8e9;
}

/* checkbox “fora” do card */
.ptc-checkbox {
  padding-right: 4px;
}

/* Card ocupa o container; sem width fixa */
.ptc-card {
  background: #ffffff;
}

/* Barra lateral 4px (única) */
.ptc-side {
  width: 4px;
  border-radius: 4px;
  margin-right: 8px;
}

/* Conteúdo com padding 16 e espaçamento 8 */
.ptc-content {
  padding-bottom: 16px;
  padding-top: 16px;
  padding-right: 16px;
  padding-left: 12px;
  gap: 8px; /* <= Figma 8 entre blocos */
  min-width: 0;
}

/* Header em grid: nome (linha 1), tag (linha 2), ações na direita (span 2 linhas) */
.ptc-header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'name actions'
    'tag  actions';
  column-gap: 8px;
  row-gap: 4px;
  align-items: start;
}

.ptc-name {
  grid-area: name;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.ptc-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: var(--opacity-text--primary, rgba(0, 0, 0, 0.87));
  text-overflow: ellipsis;

  /* subtitle-1/subtitle-1, medium */
  font-family: var(--fontFamilies-roboto, Roboto);
  font-size: var(--fontSize-42, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--lineHeights-42, 28px); /* 175% */
  letter-spacing: 0.024px;
}

/* tag sempre abaixo */
.ptc-tag {
  grid-area: tag;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ptc-actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Datas em 2 colunas com gap 8 (Figma) */
.ptc-dates {
  display: flex;
  gap: 8px;
}
.ptc-date-col {
  flex: 1 1 0;
  min-width: 0;
}

.ptc-bottom {
  justify-content: space-between;
  gap: 8px;
}

.ptc-status {
  flex: 0 0 140px; /* parecido com seu max-width anterior */
  min-width: 0;
}

.ptc-action {
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

/* Botão “pill” como no seu */
.ptc-pill {
  border-radius: 9999px;
  background-color: #e3f2fd;
  color: #2196f3;
  text-transform: none;
}

/* tipografia (mantive as suas) */
.ptc-caption {
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));

  /* caption/caption */
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: var(--fontSize-77, 12px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--lineHeights-63, 20px); /* 166.667% */
  letter-spacing: 0.048px;
}

.ptc-label {
  font-weight: 500;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-error {
  color: #d32f2f !important;
}
.text-success {
  color: #4caf50 !important;
}

.ptc-card--disabled {
  opacity: 0.7;
}

.ptc-divider {
  margin-top: 8px; /* Figma */
  margin-bottom: 8px; /* Figma */
}
</style>
